/* @HEADER
 * @version 1.1.1422 | 2026-11-01
 * @purpose Describe the role of main in this repository.
 * @sidecar main.tsx.header.md
 * @layer app | @hex _none_ | @ctx _none_
 * @public false
 * @edit careful
 */

// SpecRefs: ZVX-DEV-8843, ZVX-DEV-6525, ZVX-DEV-6523, ZVX-DEV-6465, ZVX-DEV-6434, ZVX-DEV-6241, ZVX-DEV-7547, ZVX-DEV-7901

import { StrictMode } from 'react';
import { createRoot } from 'react-dom/client';
import './index.css';
import '@zvenix/tasks/styles/board.css'; // ZVX-DEV-1810: single-source kanban board styles
import App from './App.tsx';
// ZVX-DEV-2740: mobile.css MUST load after App (and its transitive CSS, e.g.
// AppShell.css) so the mobile-override layer is injected LAST in the cascade.
// Media queries add no specificity — with equal-specificity rules (e.g. both
// `.sidebar { display: ... }` at (0,1,0)) the later-injected rule wins
// regardless of viewport. Importing mobile.css before App made desktop's
// `.sidebar { display: flex }` win over mobile's `@media (max-width:639px)
// { .sidebar { display: none } }`, leaking the 200px desktop sidebar onto
// mobile viewports and shoving mobile content off-screen.
import './styles/mobile.css';
import { ErrorBoundary } from './app/ErrorBoundary.tsx';
import { MessagesProvider } from './shared/i18n/MessagesContext';
import { ConsentBanner } from './features/analytics/public-api';
import { UpdateAvailableNotice } from './pwa/UpdateAvailableNotice';
import { FeedbackWidget } from './features/feedback/FeedbackWidget';
import { captureShellForUpdate } from './pwa/shellSnapshot';
import { installShellSnapshotCapture } from './pwa/shellSnapshotCapture';
import { setDbName, setProjectKey, resolveDbName } from './lib/db/index.ts';
import {
  ensureDefaultBoardGroup,
  ensureDefaultBoard,
  ensureAllTasksSpace,
  ensureAllTasksDefaultBoard,
} from './lib/db/index.ts';
import { isBenignDbClosingError } from './lib/db/dbErrors.ts';
import { getActiveProject, resolveProjectPrefix } from './lib/projectStore.ts';
import { installMaintenanceHandle } from './lib/db/keyReissue.ts'; // ZVX-DEV-6465
import { installDbLockHandle } from './lib/db/dbLock.ts'; // ZVX-DEV-6523
import { installDbDiagnosticHandle } from './lib/db/dbDiagnosticHandle.ts'; // ZVX-DEV-6525
import { completeDeferredWipe } from './lib/signOutWipeHost.ts';
import appVersion from 'virtual:app-version';
import { registerSW } from './registerSW';
// ZVX-DEV-3000 -- thread the build version into the p2p telemetry client's
// dump()/download() header ONCE at boot, so a collected dump always
// self-identifies which build produced it. Cheap regardless of the telemetry
// flag (a string assignment, mirrors setEndpoint's always-safe-to-call
// pattern in runContentP2pAutoSync.mjs) -- the realtime module itself stays
// Vite-agnostic and never imports this virtual module directly.
import { getP2pTelemetryClient } from '@modules/realtime/public-api.mjs';
// ZVX-DEV-3463 -- subscribe window 'error'/'unhandledrejection' into the SAME
// p2p telemetry client, ONCE at boot. Genuinely inert (zero listeners
// registered) when the p2p/p2p-verbose flag is off -- see this file's header.
import { installUncaughtErrorTelemetry } from './features/telemetry/public-api.mjs';
import { startDiagnosticRecorder, readBootInfo, readBrowserMemory, isConsentCurrent, subscribePerformanceEntries } from './features/telemetry/public-api.mjs'; // ZVX-DEV-7261, ZVX-DEV-7270, ZVX-DEV-7276, ZVX-DEV-7811
import { setCanvasSaveObserver } from '@zvenix/kanvas'; // ZVX-DEV-7276
// ZVX-DEV-7901 -- what is erased when a DIFFERENT person signs in (and by the sign-out wipe) is registered by modules that register as they load,
// so these two load at boot, before the first sign-in, and not when the first screen that uses them opens.
import './features/feedback/recorderDeviceReset';
import './features/feedback/lastSessionReset';
import { setUiActionObserver } from '@zvenix/tasks'; // ZVX-DEV-7676
import { setDbObserver } from './lib/db/dbObserver.mjs'; // ZVX-DEV-7276
import { getDbName } from './lib/db/core.ts'; // ZVX-DEV-7276
// ZVX-DEV-5548 -- point the shared telemetry shipper at THIS build's api origin
// BEFORE anything emits. ZVX-DEV-2933 built `setEndpoint` for this, but the only
// caller sits inside one content type's mount, behind that mount's enable-check
// and after its enabled/userId gate, so everything emitted before or without
// that mount went to the SPA's own origin -- where the static site answers POST
// with 405. Measured on production 2026-09-03, and the reason a content-relay
// investigation had no client-side data at all.
import { configureTelemetryEndpoint } from './features/telemetry/public-api.mjs';
import { readRecorderPlace } from './app/recorderPlace'; // ZVX-DEV-7882
import { resolveApiBase } from './shared/config/apiConfig';
import { installClientVersionOnFetch } from './lib/clientVersion';
import { appClock, installAppClockOnFetch } from './lib/appClock';
import { setWriterVersion } from './features/content-p2p/writerVersion';
// ZVX-DEV-3679 -- name, on live traffic, every card or edit field this device
// sends that no wire list accounts for. The declared lists (ZVX-DEV-3626/3674)
// close the future; only real traffic can show a field that has been travelling
// undeclared all along. Changes nothing about what is sent — and the observer's
// own enable-check is the SAME one the telemetry client uses, so an unflagged
// build pays one comparison per payload and nothing else.
import { createWireFieldDriftObserver } from './features/content-p2p/wireFieldDriftObserver.mjs';
import { setOutgoingWireObserver } from './features/content-p2p/taskSyncEnvelope.mjs';
import { isP2pTelemetryEnabled } from '@modules/realtime/public-api.mjs';
// ZVX-DEV-4450 -- the SAME generator the connection and the catalog write
// already agree on (ZVX-DEV-4418), so a telemetry event, a wire body and a
// signalling connection all name one tab identically.
import { resolveTabInstanceId, tabInstanceIdReady, createLiveTabsReader } from './features/content-p2p/tabInstanceId.mjs';
import { installLeaveFlush } from './lib/backupLeaveFlush'; // ZVX-DEV-8843

// ZVX-DEV-6127 -- finish what the last sign-out could not delete. Issued
// SYNCHRONOUSLY here, before anything opens a database: IndexedDB serves a
// delete request queued before an open on the same name first, so the open
// that follows meets an empty database instead of blocking the delete again.
// A name that still cannot go stays written down for the next boot.
completeDeferredWipe().catch((err) => console.warn('[sign-out-wipe] deferred wipe skipped:', err));

const activeProject = getActiveProject();
setDbName(resolveDbName(activeProject)); // ZVX-DEV-3102: deterministic org-project dbName
setProjectKey(resolveProjectPrefix(activeProject)); // ZVX-DEV-3221: Latin initials/prefix fallback, not the literal 'ZVX'
// ZVX-DEV-6465 -- the operator's DevTools handle for the one-shot key re-issue / move-home repairs.
installMaintenanceHandle();
// ZVX-DEV-6523 -- window.__zvenixDbLock.holder(): who holds the db lock, and for how long.
installDbLockHandle();
// ZVX-DEV-6525 -- window.__zvenixDb: the ambient database pointer, the registry's answer, the space page's own read.
installDbDiagnosticHandle();

configureTelemetryEndpoint(getP2pTelemetryClient(), resolveApiBase()); // ZVX-DEV-5548

const version = appVersion || 'dev';
getP2pTelemetryClient().setAppVersion(version); // ZVX-DEV-3000
// ZVX-DEV-6234 -- every body this client sends over the relay names the build
// that wrote it; set once here, read by the envelope builders.
setWriterVersion(appVersion || null);
// ZVX-DEV-6171 -- every api request names this build, so the server's census
// of client versions is a fact and not a guess. Installed once, decides by URL.
installClientVersionOnFetch(window, { apiBase: resolveApiBase(), version: appVersion || null });
// ZVX-DEV-7547: every API answer carries the server's time (X-Server-Time); the app's own clock is measured from it.
installAppClockOnFetch(window, { apiBase: resolveApiBase(), clock: appClock, perfNow: () => performance.now() });
// ZVX-DEV-4450 -- label every telemetry event with the tab that produced it.
// Same one-line-at-boot shape as setAppVersion above, and the same reason: a
// reading that spans two tabs of one account could not say which tab did what,
// which is how a delete published by tab A and a "removed nothing" reported by
// tab B were once joined into a conclusion a probe then disproved.
// ZVX-DEV-7989 -- the tab's OWN id (a duplicated tab starts with the original's until its claim settles, one task).
void tabInstanceIdReady.then((tabId) => getP2pTelemetryClient().setInstanceId(tabId)); // ZVX-DEV-4450
installUncaughtErrorTelemetry(getP2pTelemetryClient()); // ZVX-DEV-3463
// ZVX-DEV-7261 -- the on-device flight recorder, installed here and not
// later because a recorder that starts when the report button is pressed has
// recorded nothing. On by the build default since `ZVX-DEV-7268`,
// because that is the first moment a person can see what it holds; an explicit
// `off` in `localStorage['zvx:recorder']` or `?recorder=` still switches it
// off. It writes to its own IndexedDB database and makes no network call.
// Started through the session wrapper (ZVX-DEV-7270) so the settings checkbox's
// confirmed modal can start it late and its untick can wipe the same store.
const readLiveTabs = createLiveTabsReader() // ZVX-DEV-8069
void tabInstanceIdReady.then(() => startDiagnosticRecorder({
  clientVersion: appVersion || null,
  sessionId: resolveTabInstanceId(),
  subscribeToP2pTelemetry: (listener) => getP2pTelemetryClient().subscribe(listener),
  // ZVX-DEV-7276 -- the extended tier's own records: the outcome of every canvas
  // save, the local database's health, and why this page was loaded. Each is a
  // seam that is empty unless the recorder sets it, and each is asked about the
  // tier before it records.
  subscribeCanvasSave: (listener) => {
    setCanvasSaveObserver(listener);
    return () => setCanvasSaveObserver(null);
  },
  // ZVX-DEV-7676 -- what the person does in the search and between boards: the seam of `@zvenix/tasks`, empty unless
  // the recorder sets it, and asked about the tier and the consent text before anything is recorded.
  subscribeUiAction: (listener) => {
    setUiActionObserver(listener);
    return () => setUiActionObserver(null);
  },
  // ZVX-DEV-8059 -- what each collection of the file backup cost: the seam of fileBackup, empty unless the recorder sets
  // it; fileBackup is loaded when the recorder starts only if it is not already, so the observer is set once it is there.
  subscribeBackupCost: (listener) => {
    let release = () => {};
    let released = false;
    void import('./lib/fileBackup').then((m) => {
      if (released) return;
      m.setBackupCostObserver(listener);
      release = () => m.setBackupCostObserver(null);
    });
    return () => {
      released = true;
      release();
    };
  },
  installDbObserver: (observer) => {
    const previous = setDbObserver(observer);
    return () => {
      setDbObserver(previous);
    };
  },
  readBootInfo,
  // ZVX-DEV-8376 (ZVX-4684) -- the main thread's own account of itself: long tasks and slow input events, counted per 30 s.
  subscribePerformance: subscribePerformanceEntries,
  // ZVX-DEV-7811 -- the heap sizes and the node count, once at start and every 30 s.
  // ZVX-DEV-8069 -- and how many tabs of this origin are alive: the heap figure of a page is the heap of the thread it shares with its siblings.
  readMemory: () => {
    const tabs = readLiveTabs()
    return tabs === null ? readBrowserMemory() : { ...readBrowserMemory(), tabs }
  },
  // ZVX-DEV-7882 -- which workspace, project, Space and board are open: ids, read when each record is written.
  readPlace: () => readRecorderPlace(),
  // ZVX-DEV-7813 -- a page that dies while visible leaves the ring as a crash snapshot for the next start; bootId is new for EVERY load, the session id is the tab's.
  markSession: {
    bootId: `${Date.now().toString(36)}-${Math.random().toString(36).slice(2, 10)}`,
    locks: typeof navigator !== 'undefined' ? navigator.locks : undefined,
    documentRef: document,
  },
  // A consent given under an older text does not cover these three kinds.
  isConsentCurrent: () => isConsentCurrent(),
  getDbName,
})); // ZVX-DEV-7261
setOutgoingWireObserver(
  // ZVX-DEV-3679
  createWireFieldDriftObserver({
    isEnabled: isP2pTelemetryEnabled,
    // Names and a surface — never a value. This is meant to be collected from a
    // real person's session, so the report must carry nothing of their content.
    emit: ({ surface, fields }) =>
      getP2pTelemetryClient().emit(() => ({
        stage: 'wire-field-drift',
        fields: { surface, names: fields.join(','), count: fields.length },
      })),
  }),
);
const mode = import.meta.env.MODE;
console.log(
  `%cZvenix v${version}%c\n` +
    `Project: ${activeProject.name} (${activeProject.dbName})\n` +
    `Mode:    ${mode}\n` +
    `Stack:   React 19 + Vite + IndexedDB\n` +
    `Arch:    COA hex (ADR-0012)`,
  'font-weight:bold;font-size:14px;color:#4f8cff',
  'font-weight:normal;font-size:12px;color:inherit',
);

Promise.all([
  ensureDefaultBoardGroup(),
  ensureDefaultBoard(),
  ensureAllTasksSpace(),
  ensureAllTasksDefaultBoard(),
])
  // ZVX-DEV-2735: a reload mid-seed races IDB teardown with a benign
  // InvalidStateError ("connection is closing/closed") — not a real failure.
  .catch((err) => {
    if (!isBenignDbClosingError(err)) console.warn('[board] seed defaults failed:', err);
  });

import('./lib/fileBackup')
  .then((m) => {
    // ZVX-DEV-6241 (C7, operator's word #15926): the one-shot cleanup of task
    // copies that other project databases still hold from before ZVX-DEV-5962,
    // sequenced after autoInitBackup — it refuses without a backup and flushes
    // one before the first delete. ZVX-DEV-7143: boot no longer stamps sync_id;
    // see bootTaskMaintenance.ts for why, and for what signs legacy tasks now.
    void import('./app/bootTaskMaintenance').then((t) =>
      t.startBootTaskMaintenance({ autoInitBackup: m.autoInitBackup }),
    );

    // ZVX-DEV-6024: the file of the user's own, outside site data. Attaches to
    // the payload seam, so every OPFS write also rewrites that file when a
    // handle is stored and its permission is still granted.
    import('./lib/userFileBackupHost')
      .then((u) => u.startUserFileBackup())
      .catch((err) => console.warn('[user-file-backup] not started:', err));

    // ZVX-DEV-2257: flush OPFS backup on page hide so debounced writes
    // (up to 1.5 s pending) are not lost when the tab is closed or navigated away.
    //
    // Event priority (most to least reliable):
    //   1. visibilitychange → hidden  — page still alive, async flush succeeds;
    //      fires before pagehide on all modern browsers; best for bfcache tabs.
    //   2. pagehide                   — reliable on mobile and bfcache scenarios.
    //   3. beforeunload               — last-resort; browser may not guarantee
    //                                   async completion, but completes if worker
    //                                   was already started.
    //
    // flushBackupOnLeave() bypasses the debounce: it cancels any pending timer and collects at once, but only when something
    // changed or the copy is older than the wait the last collection earned (ZVX-DEV-8035); the events share one collection.
    // ZVX-DEV-8843 -- hiding (the page is alive) collects by the worker and reads only what was written since; pagehide and
    // beforeunload (the page is going) write on the main thread, as before. installLeaveFlush says which event is which.
    installLeaveFlush((cause) => m.flushBackupOnLeave(cause), document, window);
  })
  .catch(() => {});

// ZVX-DEV-4305 -- retire drafts nobody will ever be offered. Unbounded keys in
// localStorage are how a feature that rescues one person's text breaks another
// feature's storage; this runs once per boot, costs one pass over the keys, and
// swallows everything (a private window that refuses storage must not stop the
// app from starting).
void import(
  // @ts-expect-error -- plain .mjs helper, no local .d.ts; see cardDraftStore.mjs's JSDoc for the rules.
  './features/kanban/cardDraftStore.mjs'
)
  .then((m) => {
    try {
      const removed = m.pruneExpiredDrafts(window.localStorage, Date.now());
      if (removed > 0) console.warn(`[card-drafts] retired ${removed} expired draft(s)`);
    } catch {
      /* storage unavailable — nothing to prune, nothing to report */
    }
  })
  .catch(() => {});

// ZVX-DEV-2343: register PWA service worker (production only — PROD guard inside)
registerSW();

// ZVX-DEV-4342 -- keep a picture of the running interface at every moment the
// page can be left, so an ORDINARY reload has something to paint.
//
// ZVX-DEV-4244 gave this protection to the update BUTTON and only to it:
// `captureShellForUpdate` had a single caller, inside `runApply()`. Measured on
// staging 0.16.1284 -> 0.16.1285 with the app open and a plain reload -- the
// ghost painted in none of 111 samples, and a screenshot at 0ms showed
// "Загружаем Zvenix…". Reloading and returning to a tab are far commoner than
// clicking an update button, so the commoner route was the unprotected one.
//
// Installed here, outside React: it must not depend on a component mounting,
// and StrictMode's double-invocation must not be able to double-wire it.
installShellSnapshotCapture({
  doc: document,
  win: window,
  capture: (at) => captureShellForUpdate(document, window.localStorage, at, version),
  now: () => Date.now(),
});

createRoot(document.getElementById('root')!).render(
  <StrictMode>
    <ErrorBoundary>
      <App />
      {/* ZVX-DEV-2814: mounted independently of App's per-route MessagesProvider
          branches so the cookie-consent banner works on every screen (landing,
          register, authenticated app) without touching App.tsx's routing. */}
      <MessagesProvider>
        <ConsentBanner />
      </MessagesProvider>
      {/* ZVX-DEV-3675: same reasoning as ConsentBanner above — mounted
          independently of App's routing so a deployed update can be
          announced on whichever screen the person is currently looking at. */}
      <MessagesProvider>
        <UpdateAvailableNotice />
      </MessagesProvider>
      {/* ZVX-DEV-6434: the «!» in the corner — same reasoning again: a word from
          any screen, so it lives above App's routing. */}
      <MessagesProvider>
        <FeedbackWidget version={version} />
      </MessagesProvider>
    </ErrorBoundary>
  </StrictMode>,
);
